// ============================================================
// PractitionerAvatar.tsx
// S4 (86d420rzv) — one shared avatar for a practitioner's stored colour + initials,
// with WCAG-contrast text. Replaces the inline coloured-circle markup that used to be
// hand-duplicated (with hardcoded white text) in StaffLegend, PractitionerCalendar
// and AppointmentListView.
// ============================================================

import React from "react";
import { TeamMember } from "../../types/admin/TeamTypes";
import { practitionerColour, practitionerInitials } from "../../utils/appointments/practitionerColour";
import { getReadableTextColor } from "../../utils/colorUtils";

// A lookup miss (e.g. an unresolved secondary/tandem practitioner) must never render as if
// it had resolved. Deliberately grey and identity-free — never the hashed fallback palette,
// which IS a resolved (if collision-prone) identity. Text colour is NOT hardcoded here —
// it runs through the same getReadableTextColor() call as every resolved practitioner, so
// this neutral background gets the same WCAG-contrast treatment (black-on-#8c8c8c, not a
// fixed white that fails AA at the 9-11px bold sizes these avatars render at).
// Exported so call sites that render their OWN unresolved-practitioner affordance (e.g. a
// calendar card's border/shadow) can key off this exact value instead of a second hardcoded
// literal — see PractitionerCalendar.tsx's import of it (Fix 1, 86d40wv5a wave 2).
export const NEUTRAL_BACKGROUND = "#8c8c8c";
const NEUTRAL_INITIALS = "?";

export type PractitionerAvatarMember = Pick<TeamMember, "id" | "team_member_info" | "first_name" | "last_name">;

export interface PractitionerAvatarProps {
	/**
	 * The resolved practitioner record. Pass `null`/`undefined` when the practitioner id
	 * could not be resolved against the roster — this renders a neutral grey "?" avatar
	 * rather than a colour that implies a (possibly wrong) identity.
	 */
	member?: PractitionerAvatarMember | null;
	/** Diameter in px. Existing call sites: 18 (tandem/secondary), 20 (calendar primary), 28 (list view), 32 (legend). */
	size?: number;
	/** Explicit font size override; defaults to a size-proportional value. */
	fontSize?: number;
	/** Merged last, so callers can add border/shadow/margin/zIndex for stacking, overlap, selection, etc. */
	style?: React.CSSProperties;
	className?: string;
	"data-testid"?: string;
}

// forwardRef so an antd <Tooltip> (used at every calendar call site) can attach its
// positioning ref directly, the same as it would to a plain host <div>.
const PractitionerAvatar = React.forwardRef<HTMLDivElement, PractitionerAvatarProps>(
	({ member, size = 28, fontSize, style, className, "data-testid": testId }, ref) => {
		const backgroundColor = member ? practitionerColour(member) : NEUTRAL_BACKGROUND;
		const initials = member ? practitionerInitials(member.first_name, member.last_name) : NEUTRAL_INITIALS;
		// One call covers both branches — getReadableTextColor already resolves correctly
		// for the neutral background, so there is no need for a separate neutral text const.
		const textColor = getReadableTextColor(backgroundColor);

		return (
			<div
				ref={ref}
				className={className}
				data-testid={testId}
				style={{
					width: size,
					height: size,
					borderRadius: "50%",
					display: "flex",
					alignItems: "center",
					justifyContent: "center",
					flexShrink: 0,
					fontWeight: 700,
					fontSize: fontSize ?? Math.max(9, Math.round(size * 0.35)),
					backgroundColor,
					color: textColor,
					...style,
				}}
			>
				{initials}
			</div>
		);
	}
);

PractitionerAvatar.displayName = "PractitionerAvatar";

export default PractitionerAvatar;
